/**
 * Copyright (C) Mairistem
 *
 * Unauthorized copying of this file, via any medium is strictly prohibited.
 *
 * Proprietary and confidential
 */

import React from 'react';

import { Form, Grid } from '@mairistem/composants';

import {
  AIDE_MATERIELLE_FIXE,
  OPTIONS_AIDE_MATERIELLE,
  OPTIONS_REGIME_BIEN,
  OPTIONS_TYPE_CONVENTION_FICHE,
  VALEUR_AIDE_MAX_LENGTH,
} from '@/constants';

import PanelModalEdition from '../../PanelModalEdition';

interface InformationsConventionConditionsModalProps {
  data: any;
  onSave: () => Promise<void>;
  onChange: (data: Record<string, unknown>) => void;
  onClose: () => void;
  onCancel: () => void;
}

const InformationsConventionConditionsModal = (
  props: InformationsConventionConditionsModalProps,
) => {
  const { data, onSave, onChange, onClose, onCancel } = props;

  const [loading, setLoading] = React.useState<boolean>(false);
  const [errors, setErrors] = React.useState<
    Record<string, string | boolean> & { api?: boolean }
  >({});

  const handleChangeField = React.useCallback(
    (field: string, value: unknown) => {
      setErrors((prev) => {
        if (!prev[field]) {
          return prev;
        }
        const { [field]: _removed, ...rest } = prev;
        return rest;
      });
      onChange({ [field]: value });
    },
    [onChange],
  );

  const handleChangeAideMaterielle = React.useCallback(
    (value: number) => {
      setErrors((prev) => {
        if (!prev.valeurAide) {
          return prev;
        }
        const { valeurAide: _removed, ...rest } = prev;
        return rest;
      });
      onChange({
        aideMaterielle: value,
        // Le montant uniquement si aide matéterielle fixé, sinon null
        ...(value !== AIDE_MATERIELLE_FIXE && { valeurAide: null }),
      });
    },
    [onChange],
  );

  const handleChangeValeurAide = React.useCallback(
    (value: string) => {
      // `maxLength` est ignoré par les input de type number
      if (value.length > VALEUR_AIDE_MAX_LENGTH) return;

      handleChangeField('valeurAide', value === '' ? null : Number(value));
    },
    [handleChangeField],
  );

  const handleSave = React.useCallback(() => {
    const valeurAideRequise =
      data?.aideMaterielle === AIDE_MATERIELLE_FIXE &&
      (data?.valeurAide === null || data?.valeurAide === undefined);

    if (valeurAideRequise) {
      setErrors({ valeurAide: "La valeur de l'aide est requise." });
      return;
    }

    setLoading(true);

    onSave()
      .then(() => {
        onClose();
      })
      .catch(() => {
        setErrors({ api: true });
      })
      .finally(() => {
        setLoading(false);
      });
  }, [onClose, onSave, data?.aideMaterielle, data?.valeurAide]);

  return (
    <PanelModalEdition
      data-testid="conditions-modal"
      errors={errors}
      loading={loading}
      header="CONDITIONS DE LA CONVENTION"
      onSave={handleSave}
      onClose={onCancel}
    >
      <Form>
        <Grid>
          <Grid.Row>
            <Grid.Column width={16}>
              <Form.Dropdown
                selection
                fluid
                label="Type de convention"
                data-testid="convention-type"
                options={OPTIONS_TYPE_CONVENTION_FICHE}
                value={data?.typeConvention ?? 0}
                onChange={(_e, target) =>
                  handleChangeField('typeConvention', Number(target?.value))
                }
              />
            </Grid.Column>
          </Grid.Row>
          <Grid.Row>
            <Grid.Column width={10}>
              <Form.Dropdown
                selection
                fluid
                label="Aide matérielle"
                data-testid="convention-aide-materielle"
                options={OPTIONS_AIDE_MATERIELLE}
                value={data?.aideMaterielle ?? 0}
                onChange={(_e, target) =>
                  handleChangeAideMaterielle(Number(target?.value))
                }
              />
            </Grid.Column>
            {data?.aideMaterielle === AIDE_MATERIELLE_FIXE && (
              <Grid.Column width={6}>
                <Form.Input
                  fluid
                  type="number"
                  label="Valeur de l'aide (€ / an)"
                  input={{ 'data-testid': 'convention-valeur-aide' }}
                  error={!!errors?.valeurAide}
                  value={data?.valeurAide ?? ''}
                  onChange={(_e, target) =>
                    handleChangeValeurAide(target.value)
                  }
                />
              </Grid.Column>
            )}
          </Grid.Row>
          <Grid.Row>
            <Grid.Column width={16}>
              <Form.Dropdown
                selection
                fluid
                label="Régime des biens"
                data-testid="convention-regime-bien"
                options={OPTIONS_REGIME_BIEN}
                value={data?.regimeBien ?? 0}
                onChange={(_e, target) =>
                  handleChangeField('regimeBien', Number(target?.value))
                }
              />
            </Grid.Column>
          </Grid.Row>
        </Grid>
      </Form>
    </PanelModalEdition>
  );
};

export default InformationsConventionConditionsModal;
